/**
 * Công thức tính lương và giờ tăng ca (hỗ trợ Ca làm việc qua đêm / Overnight Shifts).
 * 
 * Quy tắc tính lương HomeTools:
 * - Lương cơ bản: Mức lương theo giờ chuẩn (ví dụ 25,500đ/h).
 * - Lương tăng ca (extra_salary trong DB): Mức phụ cấp tăng ca cộng thêm trên mỗi giờ tăng ca (ví dụ 4,500đ/h).
 * - 1 giờ tăng ca sẽ nhận = Lương cơ bản + Phụ cấp tăng ca (ví dụ 25,500đ + 4,500đ = 30,000đ/h).
 * 
 * Công thức:
 * Tổng lương = (Tổng giờ làm × Lương cơ bản) + (Tổng giờ tăng ca × Phụ cấp tăng ca)
 */

export interface CheckRecordForSalary {
  check_type: 'check_in' | 'check_out';
  check_time: string | Date;
}

const NOON_HOUR = 12;
const OVERTIME_AFTER_EARLY_CHECKIN = 17; // check-in trước 12h → tăng ca sau 17h
const OVERTIME_AFTER_LATE_CHECKIN = 18;  // check-in từ 12h trở đi → tăng ca sau 18h

export interface Holiday {
  id?: string;
  holiday_date: string; // YYYY-MM-DD
  name: string;
  multiplier: number;
}

export interface DaySalaryDetail {
  date: string;
  hours: number;
  extraHours: number;
  multiplier: number;
  isHoliday: boolean;
  holidayName?: string | undefined;
  daySalary: number;
}

interface ShiftPair {
  checkIn: Date;
  checkOut: Date;
  dateKey: string; // Ngày theo lượt check-in (YYYY-MM-DD)
}

/**
 * Ghép cặp các lượt Check-in và Check-out theo thứ tự thời gian (hỗ trợ ca qua đêm).
 */
function pairCheckInOutRecords(records: CheckRecordForSalary[]): ShiftPair[] {
  const sortedRecords = [...records].sort((a, b) => {
    const tA = a.check_time instanceof Date ? a.check_time.getTime() : new Date(a.check_time).getTime();
    const tB = b.check_time instanceof Date ? b.check_time.getTime() : new Date(b.check_time).getTime();
    return tA - tB;
  });

  const pairs: ShiftPair[] = [];
  let pendingCheckIn: Date | null = null;

  for (const r of sortedRecords) {
    const d = r.check_time instanceof Date ? r.check_time : new Date(r.check_time);

    if (r.check_type === 'check_in') {
      pendingCheckIn = d;
    } else if (r.check_type === 'check_out') {
      if (pendingCheckIn) {
        const diffHours = (d.getTime() - pendingCheckIn.getTime()) / (1000 * 60 * 60);
        // Ca làm việc hợp lệ: check-out sau check-in và tổng thời gian ca <= 24 giờ
        if (diffHours > 0 && diffHours <= 24) {
          const inDateKey = `${pendingCheckIn.getFullYear()}-${String(pendingCheckIn.getMonth() + 1).padStart(2, '0')}-${String(pendingCheckIn.getDate()).padStart(2, '0')}`;
          pairs.push({
            checkIn: pendingCheckIn,
            checkOut: d,
            dateKey: inDateKey
          });
          pendingCheckIn = null; // Hoàn thành ghép cặp 1 ca
        }
      }
    }
  }

  return pairs;
}

/**
 * Tính tổng giờ tăng ca từ danh sách check-in/check-out (của một nhân viên).
 */
export function calculateTotalExtraHours(records: CheckRecordForSalary[]): number {
  const pairs = pairCheckInOutRecords(records);
  let totalExtra = 0;

  for (const pair of pairs) {
    const hasCheckInBeforeNoon = pair.checkIn.getHours() < NOON_HOUR;
    const thresholdHour = hasCheckInBeforeNoon ? OVERTIME_AFTER_EARLY_CHECKIN : OVERTIME_AFTER_LATE_CHECKIN;

    const thresholdTime = new Date(pair.checkIn);
    thresholdTime.setHours(thresholdHour, 0, 0, 0);

    if (pair.checkOut.getTime() > thresholdTime.getTime()) {
      const overtimeStart = Math.max(pair.checkIn.getTime(), thresholdTime.getTime());
      const overtimeMs = Math.max(0, pair.checkOut.getTime() - overtimeStart);
      totalExtra += overtimeMs / (1000 * 60 * 60);
    }
  }

  return totalExtra;
}

/**
 * Tính tổng lương: (tổng giờ làm × lương cơ bản) + (tổng giờ tăng ca × phụ cấp tăng ca).
 */
export function calculateTotalSalary(
  totalHours: number,
  totalExtraHours: number,
  basicSalary: number,
  extraSalary: number
): number {
  return totalHours * basicSalary + totalExtraHours * extraSalary;
}

/**
 * Tính lương chi tiết theo từng ngày, áp dụng hệ số ngày lễ nếu có (hỗ trợ ca qua đêm).
 */
export function calculateSalaryDetailed(
  records: CheckRecordForSalary[],
  basicSalary: number,
  extraSalary: number,
  holidays: Holiday[]
): {
  totalHours: number;
  totalExtraHours: number;
  totalSalary: number;
  details: DaySalaryDetail[];
} {
  const pairs = pairCheckInOutRecords(records);

  // Nhóm các ca ghép cặp theo Ngày của Check-in
  const byDatePairs = new Map<string, ShiftPair[]>();
  for (const pair of pairs) {
    if (!byDatePairs.has(pair.dateKey)) {
      byDatePairs.set(pair.dateKey, []);
    }
    byDatePairs.get(pair.dateKey)!.push(pair);
  }

  let totalHours = 0;
  let totalExtraHours = 0;
  let totalSalary = 0;
  const details: DaySalaryDetail[] = [];

  const sortedDateKeys = Array.from(byDatePairs.keys()).sort();

  for (const dateKey of sortedDateKeys) {
    const dateShiftPairs = byDatePairs.get(dateKey)!;

    let dayTotalHours = 0;
    let dayExtraHours = 0;

    for (const pair of dateShiftPairs) {
      const shiftDurationHours = (pair.checkOut.getTime() - pair.checkIn.getTime()) / (1000 * 60 * 60);
      dayTotalHours += shiftDurationHours;

      const hasCheckInBeforeNoon = pair.checkIn.getHours() < NOON_HOUR;
      const thresholdHour = hasCheckInBeforeNoon ? OVERTIME_AFTER_EARLY_CHECKIN : OVERTIME_AFTER_LATE_CHECKIN;

      const thresholdTime = new Date(pair.checkIn);
      thresholdTime.setHours(thresholdHour, 0, 0, 0);

      if (pair.checkOut.getTime() > thresholdTime.getTime()) {
        const overtimeStart = Math.max(pair.checkIn.getTime(), thresholdTime.getTime());
        const overtimeMs = Math.max(0, pair.checkOut.getTime() - overtimeStart);
        dayExtraHours += overtimeMs / (1000 * 60 * 60);
      }
    }

    // Kiểm tra ngày lễ theo ngày check-in
    const holiday = holidays.find(h => h.holiday_date === dateKey);
    const isHoliday = !!holiday;
    const multiplier = holiday ? holiday.multiplier : 1.0;
    const holidayName = holiday ? holiday.name : undefined;

    // Tính lương ngày: (Tổng giờ làm * Lương cơ bản + Giờ tăng ca * Phụ cấp tăng ca) * hệ số ngày lễ
    const daySalary = (dayTotalHours * basicSalary + dayExtraHours * extraSalary) * multiplier;

    totalHours += dayTotalHours;
    totalExtraHours += dayExtraHours;
    totalSalary += daySalary;

    details.push({
      date: dateKey,
      hours: Math.max(0, Math.round(dayTotalHours * 100) / 100),
      extraHours: Math.max(0, Math.round(dayExtraHours * 100) / 100),
      multiplier,
      isHoliday,
      holidayName,
      daySalary: Math.max(0, Math.round(daySalary))
    });
  }

  return {
    totalHours: Math.max(0, Math.round(totalHours * 100) / 100),
    totalExtraHours: Math.max(0, Math.round(totalExtraHours * 100) / 100),
    totalSalary: Math.max(0, Math.round(totalSalary)),
    details
  };
}
